import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, map, tap, catchError, throwError, filter, take } from 'rxjs';
import { SavedReport, ReportEntityType, ReportGridState } from '../shared/models/saved-report.model';
import { UsersService } from './entity-services/users.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  private reportsSubject = new BehaviorSubject<SavedReport[]>([]);
  public readonly reports$ = this.reportsSubject.asObservable();

  private http = inject(HttpClient);
  private usersService = inject(UsersService);

  /** List all reports accessible to the current user */
  getAllAccessibleReports(): Observable<SavedReport[]> {
    return new Observable<SavedReport[]>(subscriber => {
      this.usersService.currentUserEmail$.pipe(filter(e => !!e), take(1)).subscribe(email => {
        this.http.post<any>(environment.URL_REPORTS_LIST, { userId: email }).pipe(
          map(response => (response.reports || []) as SavedReport[]),
          tap(reports => this.reportsSubject.next(reports)),
          catchError(error => {
            console.error('[ReportsService] Failed to fetch reports:', error);
            return of([] as SavedReport[]);
          })
        ).subscribe(reports => {
          subscriber.next(reports);
          subscriber.complete();
        });
      });
    });
  }

  /** Get a single report by ID */
  getReport(id: string): Observable<SavedReport | null> {
    return this.http.get<any>(environment.URL_REPORTS_GET_REPORT + id).pipe(
      map(response => (response.report || response) as SavedReport),
      catchError(error => {
        console.error('[ReportsService] Failed to fetch report:', error);
        return of(null);
      })
    );
  }

  /** Create or update a report */
  saveReport(report: Partial<SavedReport>): Observable<SavedReport> {
    return new Observable<SavedReport>(subscriber => {
      this.usersService.currentUserEmail$.pipe(filter(e => !!e), take(1)).subscribe(email => {
        const body: any = {
          name: report.name,
          description: report.description || '',
          entityType: report.entityType,
          gridState: report.gridState,
          selectedColumnIds: report.selectedColumnIds || [],
          owner: email,
          sharedWith: report.sharedWith || [],
          isPublic: report.isPublic || false,
        };

        // Include reportId only for updates
        if (report.reportId) {
          body.reportId = report.reportId;
        }

        if (report.schedule) {
          body.schedule = report.schedule;
        }

        this.http.post<any>(environment.URL_REPORTS_WRITE, body).pipe(
          catchError(error => {
            console.error('[ReportsService] Failed to save report:', error);
            return throwError(() => error);
          })
        ).subscribe(response => {
          const saved: SavedReport = response.report || { ...body, reportId: response.reportId };
          subscriber.next(saved);
          subscriber.complete();
        });
      });
    });
  }

  /** Delete a report by ID (owner only) */
  deleteReport(id: string): Observable<void> {
    return new Observable<void>(subscriber => {
      this.usersService.currentUserEmail$.pipe(filter(e => !!e), take(1)).subscribe(email => {
        this.http.delete<any>(environment.URL_REPORTS_DELETE_REPORT + id, {
          body: { owner: email }
        }).pipe(
          catchError(error => {
            console.error('[ReportsService] Failed to delete report:', error);
            return throwError(() => error);
          })
        ).subscribe(() => {
          const reports = this.reportsSubject.value.filter(r => r.reportId !== id);
          this.reportsSubject.next(reports);
          subscriber.next(undefined);
          subscriber.complete();
        });
      });
    });
  }

  /** Duplicate a report (create a copy with a new backend-generated ID) */
  duplicateReport(id: string): Observable<SavedReport> {
    return new Observable<SavedReport>(subscriber => {
      this.getReport(id).pipe(take(1)).subscribe(original => {
        if (!original) {
          subscriber.error(new Error('Report not found'));
          return;
        }

        this.saveReport({
          name: `${original.name} (Copy)`,
          description: original.description,
          entityType: original.entityType,
          gridState: original.gridState,
          selectedColumnIds: original.selectedColumnIds,
          sharedWith: [],
          isPublic: false,
        }).subscribe(newReport => {
          subscriber.next(newReport);
          subscriber.complete();
        });
      });
    });
  }

  /** Update sharing for a report (replaces the entire sharedWith array) */
  shareReport(id: string, sharedWith: string[]): Observable<void> {
    return this.http.post<any>(environment.URL_REPORTS_SHARE_REPORT + id + '/share', {
      sharedWith
    }).pipe(
      map(() => undefined),
      catchError(error => {
        console.error('[ReportsService] Failed to share report:', error);
        return throwError(() => error);
      })
    );
  }

  /** Send a conversational AI prompt to generate/modify a report grid state */
  generateAiReport(messages: AiChatMessage[], entityType: ReportEntityType): Observable<AiReportResponse> {
    const url = (environment as any).URL_REPORTS_AI_GENERATE;
    if (!url) {
      return throwError(() => new Error('AI report generation endpoint not configured'));
    }

    return this.http.post<AiReportResponse>(url, { messages, entityType }).pipe(
      catchError(error => {
        console.error('[ReportsService] AI report generation failed:', error);
        return throwError(() => error);
      })
    );
  }
}

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiReportResponse {
  gridState: ReportGridState;
  message: string;
  intent: 'new' | 'modify' | 'ambiguous';
  suggestedEntityType?: ReportEntityType;
}
